import AddressAppResource from './AddressAppResource';
import AppResource from '@milestoneslabs/milestones-js-engine/src/Data/AppResource';
import CalendarAppResource from './CalendarAppResource';
import CalendarEventAppResource from './CalendarEventAppResource';
import EmailAppResource from './EmailAppResource';
import PhoneAppResource from './PhoneAppResource';
import PropertyInstanceAppResource from './PropertyInstanceAppResource';
import TaskAppResource from './TaskAppResource';
import WildAppResource from './WildAppResource';
import OrganizationAppResource from './OrganizationAppResource'
import WorkspaceAppResource from './WorkspaceAppResource'
import Engine from '@milestoneslabs/milestones-js-engine';
import PropertyValuationAppResource from "./PropertyValuationAppResource";
import isEmpty from 'lodash/isEmpty';
import get from "lodash/get";

class PropertyAppResource extends AppResource {

    /**
     * @returns {string}
     */
    appResourceType() {
        return 'Property';
    }

    /**
     * @returns {string}
     */
    appResourceApiPrefix() {
        return 'properties';
    }

    /**
     * @returns {*[]}
     */
    hidden() {
        return super.hidden().concat([
            'uuid',
            'deleted_at',
            'created_at',
        ]);
    }

    /**
     * @returns {*[]}
     */
    fillable() {
        return [
            'name',
            'yearBuilt',
            'noOfUnits',
            'price',
            'type',
            'subtype',
            'status',
            'bedrooms',
            'bathrooms',
            'size',
            'sizeUnits',
            'lotSize',
            'lotSizeUnits',
            'details',
            'importSource',
            'addressUuid',
            'parentPropertyUuid',
            'parentProperty',
            'organizationUuid',
            'organization',
            'workspaceUuid',
            'workspace',
            'propertyVisibilityUuid',
            'propertyVisibility',
            'address',
            'instances',
            'listings',
            'transactions',
            'files',
            'calendars',
            'calendarEvents',
            'channels',
            'tasks',
            'emails',
            'phones',
            'addresses',
            'status',
            'type',
            'subtype',
            'size',
            'sizeUnits',
            'lotSize',
            'lotSizeUnits',
            'images',
            'originalChangedAt',
            'location',
            'offMarketAt',
            'listingType',
            'stories',
        ];
    }

    /**
     * @returns {*[]}
     */
    relationMap() {
        return {
            parentProperty: PropertyAppResource,
            organization: OrganizationAppResource,
            workspace: WorkspaceAppResource,
            address: AddressAppResource,
            instances: PropertyInstanceAppResource,
            listings: WildAppResource,
            transactions: WildAppResource,
            files: WildAppResource,
            emails: EmailAppResource,
            phones: PhoneAppResource,
            addresses: AddressAppResource,
            calendars: CalendarAppResource,
            calendarEvents: CalendarEventAppResource,
            channels: WildAppResource,
            tasks: TaskAppResource,
            valuations: PropertyValuationAppResource,
            lastValuation: PropertyValuationAppResource,
        };
    }

    /**
     * @returns {*[]}
     */
    relationForeignKeys() {
        return {
            parentProperty: 'parent_property_uuid',
            address: 'address_uuid',
            organization: 'organization_uuid',
            workspace: 'workspace_uuid',
        };
    }

    /**
     * @returns {*}
     */
    validationRules() {
        return {
            name: {
                type: 'string',
                presence: true,
                length: {
                    minimum: 1,
                    maximum: 255,
                },
            },
            sqFeet: {
                numericality: true,
            },
            yearBuilt: {
                numericality: true,
            },
            noOfUnits: {
                numericality: true,
            },
            parentPropertyUuid: {
                type: 'string',
            },
            addressUuid: {
                type: 'string',
            },
            type: {
                type: 'string',
            },
            subtype: {
                type: 'string',
            },
            status: {
                type: 'string',
            },
            size: {
                numericality: true,
            },
            sizeUnits: {
                type: 'string',
            },
            lotSize: {
                numericality: true,
            },
            lotSizeUnits: {
                type: 'string',
            },
            details: {
                presence: true,
            },
            'details.cbsa': {
                type: 'string',
            },
            'details.submarket': {
                type: 'string',
            },
            'details.areaCode': {
                type: 'string',
            },
            'details.ZIPInfoMarket': {
                type: 'string',
            },
            'details.countryFIPS': {
                type: 'string',
            },
            'details.neighborhood': {
                type: 'string',
            },
            'details.timezone': {
                type: 'string',
            },
            'details.lotZillowMarket': {
                type: 'string',
            },
            'details.postalCodeLatitude': {
                type: 'string',
            },
            'details.postalCodeLongitude': {
                type: 'string',
            },
            'details.carrierRoute': {
                type: 'string',
            },
            'details.city': {
                type: 'string',
            },
            'details.cityRegion': {
                type: 'string',
            },
            'details.continentRegion': {
                type: 'string',
            },
            'details.country': {
                type: 'string',
            },
            'details.countryRegion': {
                type: 'string',
            },
            'details.countyOrParish': {
                type: 'string',
            },
            'details.crossStreet': {
                type: 'string',
            },
            'details.directions': {
                type: 'string',
            },
            'details.internetAddressDisplayYn': {
                type: 'string',
            },
            'details.latitude': {
                numericality: true,
            },
            'details.longitude': {
                numericality: true,
            },
            'details.mapCoordinate': {
                type: 'string',
            },
            'details.mapCoordinateSource': {
                type: 'string',
            },
            'details.mapUrl': {
                type: 'string',
            },
            'details.postalCity': {
                type: 'string',
            },
            'details.postalCode': {
                type: 'string',
            },
            'details.postalCodePlus4': {
                type: 'string',
            },
            'details.stateOrProvince': {
                type: 'string',
            },
            'details.stateRegion': {
                type: 'string',
            },
            'details.streetAdditionalInfo': {
                type: 'string',
            },
            'details.streetDirPrefix': {
                type: 'string',
            },
            'details.streetDirSuffix': {
                type: 'string',
            },
            'details.streetName': {
                type: 'string',
            },
            'details.streetNumber': {
                type: 'string',
            },
            'details.streetNumberNumeric': {
                numericality: true,
            },
            'details.streetSuffix': {
                type: 'string',
            },
            'details.streetSuffixModifier': {
                type: 'string',
            },
            'details.subdivisionName': {
                type: 'string',
            },
            'details.township': {
                type: 'string',
            },
            'details.unparsedAddress': {
                type: 'string',
            },
            'details.bathroomsTotalInteger': {
                numericality: true,
            },
            'details.bathroomsFull': {
                numericality: true,
            },
            'details.bathroomsHalf': {
                numericality: true,
            },
            'details.bathroomsOneQuarter': {
                numericality: true,
            },
            'details.bathroomsPartial': {
                numericality: true,
            },
            'details.bathroomsThreeQuarter': {
                numericality: true,
            },
            'details.bathroomsTotalDecimal': {
                numericality: true,
            },
            'details.mainLevelBathrooms': {
                numericality: true,
            },
            'details.bedroomsTotal': {
                numericality: true,
            },
            'details.bedroomsPossible': {
                numericality: true,
            },
            'details.mainLevelBedrooms': {
                numericality: true,
            },
            'details.aboveGradeFinishedArea': {
                numericality: true,
            },
            'details.aboveGradeFinishedAreaSource': {
                type: 'string',
            },
            'details.aboveGradeFinishedAreaUnits': {
                type: 'string',
            },
            'details.belowGradeFinishedArea': {
                numericality: true,
            },
            'details.belowGradeFinishedAreaSource': {
                type: 'string',
            },
            'details.belowGradeFinishedAreaUnits': {
                type: 'string',
            },
            'details.livingArea': {
                numericality: true,
            },
            'details.livingAreaSource': {
                type: 'string',
            },
            'details.livingAreaUnits': {
                type: 'string',
            },
            'details.lotSizeAcres': {
                numericality: true,
            },
            'details.lotSizeSquareFeet': {
                numericality: true,
            },
            'details.lotSizeArea': {
                numericality: true,
            },
            'details.lotSizeSource': {
                type: 'string',
            },
            'details.lotSizeUnits': {
                type: 'string',
            },
            'details.lotSizeDimensions': {
                type: 'string',
            },
            'details.lotDimensionsSource': {
                type: 'string',
            },
            'details.numberOfBuildings': {
                numericality: true,
            },
            'details.rangeArea': {
                numericality: true,
            },
            'details.builderModel': {
                type: 'string',
            },
            'details.builderName': {
                type: 'string',
            },
            'details.yearBuilt': {
                numericality: true,
            },
            'details.yearBuiltDetails': {
                type: 'string',
            },
            'details.yearBuiltEffective': {
                numericality: true,
            },
            'details.yearBuiltSource': {
                type: 'string',
            },
            'details.newConstructionYn': {
                type: 'string',
            },
            'details.ownership': {
                type: 'string',
            },
            'details.propertyAttachedYn': {
                type: 'string',
            },
            'details.propertyType': {
                type: 'string',
            },
            'details.propertySubType': {
                type: 'string',
            },
            'details.leaseConsideredYn': {
                type: 'string',
            },
            'details.propertyUniversalId': {
                type: 'string',
            },
            'details.stories': {
                numericality: true,
            },
            'details.storiesTotal': {
                numericality: true,
            },
            'details.foundationArea': {
                numericality: true,
            },
            'details.availabilityDate': {
                type: 'string',
            },
            'details.poolPrivateYn': {
                type: 'string',
            },
            'details.fireplacesTotal': {
                numericality: true,
            },
            'details.fireplaceYn': {
                type: 'string',
            },
            'details.roomTypeArea': {
                numericality: true,
            },
            'details.roomTypeAreaSource': {
                type: 'string',
            },
            'details.roomTypeAreaUnits': {
                type: 'string',
            },
            'details.roomTypeDescription': {
                type: 'string',
            },
            'details.roomTypeDimensions': {
                type: 'string',
            },
            'details.roomTypeLength': {
                numericality: true,
            },
            'details.roomTypeLengthWidthSource': {
                type: 'string',
            },
            'details.roomTypeLengthWidthUnits': {
                type: 'string',
            },
            'details.roomTypeLevel': {
                type: 'string',
            },
            'details.roomTypeWidth': {
                numericality: true,
            },
            'details.roomsTotal': {
                numericality: true,
            },
            'details.spaYN': {
                type: 'boolean',
            },
            'details.attachedGarageYn': {
                type: 'string',
            },
            'details.parkingTotal': {
                numericality: true,
            },
            'details.coveredSpaces': {
                numericality: true,
            },
            'details.openParkingSpaces': {
                numericality: true,
            },
            'details.carportYn': {
                type: 'string',
            },
            'details.carportSpaces': {
                numericality: true,
            },
            'details.garageYn': {
                type: 'string',
            },
            'details.garageSpaces': {
                numericality: true,
            },
            'details.openParkingYn': {
                type: 'string',
            },
            'details.otherParking': {
                type: 'string',
            },
            'details.coolingYn': {
                type: 'string',
            },
            'details.electricOnPropertyYn': {
                type: 'string',
            },
            'details.heatingYn': {
                type: 'string',
            },
            'details.powerProductionTypeAnnual': {
                numericality: true,
            },
            'details.powerProductionTypeAnnualStatus': {
                type: 'string',
            },
            'details.powerProductionTypeSize': {
                numericality: true,
            },
            'details.powerProductionTypeYearInstall': {
                numericality: true,
            },
            'details.trashExpense': {
                numericality: true,
            },
            'details.waterSewerExpense': {
                numericality: true,
            },
            'details.greenVerificationTypeBody': {
                type: 'string',
            },
            'details.greenVerificationTypeMetric': {
                numericality: true,
            },
            'details.greenVerificationTypeRating': {
                type: 'string',
            },
            'details.greenVerificationTypeSource': {
                type: 'string',
            },
            'details.greenVerificationTypeStatus': {
                type: 'string',
            },
            'details.greenVerificationTypeUrl': {
                type: 'string',
            },
            'details.greenVerificationTypeVersion': {
                type: 'string',
            },
            'details.greenVerificationTypeYear': {
                numericality: true,
            },
            'details.additionalParcelsDescription': {
                type: 'string',
            },
            'details.additionalParcelsYn': {
                type: 'string',
            },
            'details.cropsIncludedYn': {
                type: 'string',
            },
            'details.cultivatedArea': {
                numericality: true,
            },
            'details.directionFaces': {
                type: 'string',
            },
            'details.elevation': {
                numericality: true,
            },
            'details.elevationUnits': {
                type: 'string',
            },
            'details.entryLocation': {
                type: 'string',
            },
            'details.entryLevel': {
                numericality: true,
            },
            'details.frontageLength': {
                type: 'string',
            },
            'details.grazingPermitsBlmYn': {
                type: 'string',
            },
            'details.grazingPermitsForestServiceYn': {
                type: 'string',
            },
            'details.grazingPermitsPrivateYn': {
                type: 'string',
            },
            'details.habitableResidenceYn': {
                type: 'string',
            },
            'details.horseYn': {
                type: 'string',
            },
            'details.irrigationWaterRightsAcres': {
                numericality: true,
            },
            'details.irrigationWaterRightsYn': {
                type: 'string',
            },
            'details.numberOfLots': {
                numericality: true,
            },
            'details.parcelNumber': {
                type: 'string',
            },
            'details.pastureArea': {
                numericality: true,
            },
            'details.publicSurveyRange': {
                type: 'string',
            },
            'details.publicSurveySection': {
                type: 'string',
            },
            'details.publicSurveyTownship': {
                type: 'string',
            },
            'details.topography': {
                type: 'string',
            },
            'details.viewYn': {
                type: 'string',
            },
            'details.waterBodyName': {
                type: 'string',
            },
            'details.waterfrontYn': {
                type: 'string',
            },
            'details.woodedArea': {
                numericality: true,
            },
            'details.zoning': {
                type: 'string',
            },
            'details.zoningDescription': {
                type: 'string',
            },
            'details.farmCreditServiceInclYn': {
                type: 'string',
            },
            'details.farmLandAreaUnits': {
                type: 'string',
            },
            'details.farmLandAreaSource': {
                type: 'string',
            },
            'details.associationFee': {
                numericality: true,
            },
            'details.associationFee2': {
                numericality: true,
            },
            'details.associationFee2Frequency': {
                type: 'string',
            },
            'details.associationFeeFrequency': {
                type: 'string',
            },
            'details.accessCode': {
                type: 'string',
            },
            'details.associationName': {
                type: 'string',
            },
            'details.associationName2': {
                type: 'string',
            },
            'details.associationPhone': {
                type: 'string',
            },
            'details.associationPhone2': {
                type: 'string',
            },
            'details.associationYn': {
                type: 'string',
            },
            'details.taxAnnualAmount': {
                numericality: true,
            },
            'details.taxAssessedValue': {
                numericality: true,
            },
            'details.taxYear': {
                numericality: true,
            },
            'details.taxBlock': {
                type: 'string',
            },
            'details.taxBookNumber': {
                type: 'string',
            },
            'details.taxLegalDescription': {
                type: 'string',
            },
            'details.taxLot': {
                type: 'string',
            },
            'details.taxMapNumber': {
                type: 'string',
            },
            'details.taxOtherAnnualAssessmentAmount': {
                numericality: true,
            },
            'details.taxParcelLetter': {
                type: 'string',
            },
            'details.taxTract': {
                type: 'string',
            },
            'details.distanceToBusComments': {
                type: 'string',
            },
            'details.distanceToBusNumeric': {
                numericality: true,
            },
            'details.distanceToBusUnits': {
                type: 'string',
            },
            'details.distanceToElectricComments': {
                type: 'string',
            },
            'details.distanceToElectricNumeric': {
                numericality: true,
            },
            'details.distanceToElectricUnits': {
                type: 'string',
            },
            'details.distanceToFreewayComments': {
                type: 'string',
            },
            'details.distanceToFreewayNumeric': {
                numericality: true,
            },
            'details.distanceToFreewayUnits': {
                type: 'string',
            },
            'details.distanceToGasComments': {
                type: 'string',
            },
            'details.distanceToGasNumeric': {
                numericality: true,
            },
            'details.distanceToGasUnits': {
                type: 'string',
            },
            'details.distanceToPhoneServiceComments': {
                type: 'string',
            },
            'details.distanceToPhoneServiceNumeric': {
                numericality: true,
            },
            'details.distanceToPhoneServiceUnits': {
                type: 'string',
            },
            'details.distanceToPlaceofWorshipComments': {
                type: 'string',
            },
            'details.distanceToPlaceofWorshipNumeric': {
                numericality: true,
            },
            'details.distanceToPlaceofWorshipUnits': {
                type: 'string',
            },
            'details.distanceToSchoolBusComments': {
                type: 'string',
            },
            'details.distanceToSchoolBusNumeric': {
                numericality: true,
            },
            'details.distanceToSchoolBusUnits': {
                type: 'string',
            },
            'details.distanceToSchoolsComments': {
                type: 'string',
            },
            'details.distanceToSchoolsNumeric': {
                numericality: true,
            },
            'details.distanceToSchoolsUnits': {
                type: 'string',
            },
            'details.distanceToSewerComments': {
                type: 'string',
            },
            'details.distanceToSewerNumeric': {
                numericality: true,
            },
            'details.distanceToSewerUnits': {
                type: 'string',
            },
            'details.distanceToShoppingComments': {
                type: 'string',
            },
            'details.distanceToShoppingNumeric': {
                numericality: true,
            },
            'details.distanceToShoppingUnits': {
                type: 'string',
            },
            'details.distanceToStreetComments': {
                type: 'string',
            },
            'details.distanceToStreetNumeric': {
                numericality: true,
            },
            'details.distanceToStreetUnits': {
                type: 'string',
            },
            'details.distanceToWaterComments': {
                type: 'string',
            },
            'details.distanceToWaterNumeric': {
                numericality: true,
            },
            'details.distanceToWaterUnits': {
                type: 'string',
            },
            'details.elementarySchool': {
                type: 'string',
            },
            'details.elementarySchoolDistrict': {
                type: 'string',
            },
            'details.middleOrJuniorSchool': {
                type: 'string',
            },
            'details.middleOrJuniorSchoolDistrict': {
                type: 'string',
            },
            'details.highSchool': {
                type: 'string',
            },
            'details.highSchoolDistrict': {
                type: 'string',
            },
            'details.walkScore': {
                numericality: true,
            },
            'details.buyerAgencyCompensation': {
                type: 'string',
            },
            'details.buyerAgencyCompensationType': {
                type: 'string',
            },
            'details.cancelationDate': {
                type: 'string',
            },
            'details.concessions': {
                type: 'string',
            },
            'details.concessionsAmount': {
                numericality: true,
            },
            'details.concessionsComments': {
                type: 'string',
            },
            'details.contingency': {
                type: 'string',
            },
            'details.contingentDate': {
                type: 'string',
            },
            'details.contractStatusChangeDate': {
                type: 'string',
            },
            'details.copyrightNotice': {
                type: 'string',
            },
            'details.disclaimer': {
                type: 'string',
            },
            'details.dualVariableCompensationYn': {
                type: 'string',
            },
            'details.exclusions': {
                type: 'string',
            },
            'details.expirationDate': {
                type: 'string',
            },
            'details.inclusions': {
                type: 'string',
            },
            'details.internetAutomatedValuationDisplayYn': {
                type: 'string',
            },
            'details.internetConsumerCommentYn': {
                type: 'string',
            },
            'details.internetEntireListingDisplayYn': {
                type: 'string',
            },
            'details.listAgentAOR': {
                type: 'string',
            },
            'details.listAgentCellPhone': {
                type: 'string',
            },
            'details.listAgentDirectPhone': {
                type: 'string',
            },
            'details.listAgentEmail': {
                type: 'string',
            },
            'details.listAgentFax': {
                type: 'string',
            },
            'details.listAgentFullName': {
                type: 'string',
            },
            'details.listAgentFirstName': {
                type: 'string',
            },
            'details.listAgentLastName': {
                type: 'string',
            },
            'details.listAgentMiddleName': {
                type: 'string',
            },
            'details.listAgentHomePhone': {
                type: 'string',
            },
            'details.listAgentKey': {
                type: 'string',
            },
            'details.listAgentKeyNumeric': {
                numericality: true,
            },
            'details.listAgentMlsId': {
                type: 'string',
            },
            'details.listAgentMobilePhone': {
                type: 'string',
            },
            'details.listAgentNamePrefix': {
                type: 'string',
            },
            'details.listAgentNameSuffix': {
                type: 'string',
            },
            'details.listAgentOfficePhone': {
                type: 'string',
            },
            'details.listAgentOfficePhoneExt': {
                type: 'string',
            },
            'details.listAgentPager': {
                type: 'string',
            },
            'details.listAgentPreferredPhone': {
                type: 'string',
            },
            'details.listAgentPreferredPhoneExt': {
                type: 'string',
            },
            'details.listAgentStateLicense': {
                type: 'string',
            },
            'details.listAgentTollFreePhone': {
                type: 'string',
            },
            'details.listAgentUrl': {
                type: 'string',
            },
            'details.listAgentVoiceMail': {
                type: 'string',
            },
            'details.listAgentVoiceMailExt': {
                type: 'string',
            },
            'details.listAOR': {
                type: 'string',
            },
            'details.listingAgreement': {
                type: 'string',
            },
            'details.listingContractDate': {
                type: 'string',
            },
            'details.listingService': {
                type: 'string',
            },
            'details.listOfficeAOR': {
                type: 'string',
            },
            'details.listOfficeEmail': {
                type: 'string',
            },
            'details.listOfficeFax': {
                type: 'string',
            },
            'details.listOfficeKey': {
                type: 'string',
            },
            'details.listOfficeKeyNumeric': {
                numericality: true,
            },
            'details.listOfficeMlsId': {
                type: 'string',
            },
            'details.listOfficeName': {
                type: 'string',
            },
            'details.listOfficePhone': {
                type: 'string',
            },
            'details.listOfficePhoneExt': {
                type: 'string',
            },
            'details.listOfficeUrl': {
                type: 'string',
            },
            'details.listPrice': {
                numericality: true,
            },
            'details.listPriceLow': {
                numericality: true,
            },
            'details.listTeamName': {
                type: 'string',
            },
            'details.lockBoxLocation': {
                type: 'string',
            },
            'details.lockBoxSerialNumber': {
                type: 'string',
            },
            'details.offMarketDate': {
                type: 'string',
            },
            'details.onMarketDate': {
                type: 'string',
            },
            'details.originalListPrice': {
                numericality: true,
            },
            'details.previousListPrice': {
                numericality: true,
            },
            'details.signOnPropertyYn': {
                type: 'string',
            },
            'details.subAgencyCompensation': {
                type: 'string',
            },
            'details.subAgencyCompensationType': {
                type: 'string',
            },
            'details.transactionBrokerCompensation': {
                type: 'string',
            },
            'details.transactionBrokerCompensationType': {
                type: 'string',
            },
            'details.withdrawnDate': {
                type: 'string',
            },
            'details.standardStatus': {
                type: 'string',
            },
            'details.coListAgentAOR': {
                type: 'string',
            },
            'details.coListAgentCellPhone': {
                type: 'string',
            },
            'details.coListAgentDirectPhone': {
                type: 'string',
            },
            'details.coListAgentEmail': {
                type: 'string',
            },
            'details.coListAgentFax': {
                type: 'string',
            },
            'details.coListAgentFullName': {
                type: 'string',
            },
            'details.coListAgentFirstName': {
                type: 'string',
            },
            'details.coListAgentLastName': {
                type: 'string',
            },
            'details.coListAgentMiddleName': {
                type: 'string',
            },
            'details.coListAgentNamePrefix': {
                type: 'string',
            },
            'details.coListAgentNameSuffix': {
                type: 'string',
            },
            'details.coListAgentHomePhone': {
                type: 'string',
            },
            'details.coListAgentKey': {
                type: 'string',
            },
            'details.coListAgentKeyNumeric': {
                numericality: true,
            },
            'details.coListAgentMlsId': {
                type: 'string',
            },
            'details.coListAgentMobilePhone': {
                type: 'string',
            },
            'details.coListAgentOfficePhone': {
                type: 'string',
            },
            'details.coListAgentOfficePhoneExt': {
                type: 'string',
            },
            'details.coListAgentPager': {
                type: 'string',
            },
            'details.coListAgentPreferredPhone': {
                type: 'string',
            },
            'details.coListAgentPreferredPhoneExt': {
                type: 'string',
            },
            'details.coListAgentStateLicense': {
                type: 'string',
            },
            'details.coListAgentTollFreePhone': {
                type: 'string',
            },
            'details.coListAgentUrl': {
                type: 'string',
            },
            'details.coListAgentVoiceMail': {
                type: 'string',
            },
            'details.coListAgentVoiceMailExt': {
                type: 'string',
            },
            'details.coListOfficeAOR': {
                type: 'string',
            },
            'details.coListOfficeEmail': {
                type: 'string',
            },
            'details.coListOfficeFax': {
                type: 'string',
            },
            'details.coListOfficeKey': {
                type: 'string',
            },
            'details.coListOfficeKeyNumeric': {
                numericality: true,
            },
            'details.coListOfficeMlsId': {
                type: 'string',
            },
            'details.coListOfficeName': {
                type: 'string',
            },
            'details.coListOfficePhone': {
                type: 'string',
            },
            'details.coListOfficePhoneExt': {
                type: 'string',
            },
            'details.coListOfficeUrl': {
                type: 'string',
            },
            'details.homeWarrantyYn': {
                type: 'string',
            },
            'details.showingAttendedYn': {
                type: 'string',
            },
            'details.showingContactName': {
                type: 'string',
            },
            'details.showingContactPhone': {
                type: 'string',
            },
            'details.showingContactPhoneExt': {
                type: 'string',
            },
            'details.showingStartTime': {
                type: 'string',
            },
            'details.showingEndTime': {
                type: 'string',
            },
            'details.showingInstructions': {
                type: 'string',
            },
            'details.showingAdvanceNotice': {
                numericality: true,
            },
            'details.originalEntryTimestamp': {
                type: 'string',
            },
            'details.cumulativeDaysOnMarket': {
                numericality: true,
            },
            'details.daysOnMarket': {
                numericality: true,
            },
            'details.listingKey': {
                type: 'string',
            },
            'details.listingKeyNumeric': {
                numericality: true,
            },
            'details.listingId': {
                type: 'string',
            },
            'details.listTeamKey': {
                type: 'string',
            },
            'details.listTeamKeyNumeric': {
                numericality: true,
            },
            'details.privateOfficeRemarks': {
                type: 'string',
            },
            'details.privateRemarks': {
                type: 'string',
            },
            'details.publicRemarks': {
                type: 'string',
            },
            'details.approvalStatus': {
                type: 'string',
            },
            'details.bridgeModificationTimestamp': {
                type: 'string',
            },
            'details.idXParticipationYn': {
                type: 'string',
            },
            'details.majorChangeTimestamp': {
                type: 'string',
            },
            'details.majorChangeType': {
                type: 'string',
            },
            'details.mLSAreaMajor': {
                type: 'string',
            },
            'details.mLSAreaMinor': {
                type: 'string',
            },
            'details.mlsStatus': {
                type: 'string',
            },
            'details.onMarketTimestamp': {
                type: 'string',
            },
            'details.offMarketTimestamp': {
                type: 'string',
            },
            'details.modificationTimestamp': {
                type: 'string',
            },
            'details.statusChangeTimestamp': {
                type: 'string',
            },
            'details.pendingTimestamp': {
                type: 'string',
            },
            'details.priceChangeTimestamp': {
                type: 'string',
            },
            'details.originatingSystemId': {
                type: 'string',
            },
            'details.originatingSystemKey': {
                type: 'string',
            },
            'details.originatingSystemName': {
                type: 'string',
            },
            'details.sourceSystemId': {
                type: 'string',
            },
            'details.sourceSystemKey': {
                type: 'string',
            },
            'details.sourceSystemName': {
                type: 'string',
            },
            'details.syndicationRemarks': {
                type: 'string',
            },
            'details.@odata.context': {
                type: 'string',
            },
            'details.@odata.id': {
                type: 'string',
            },
            'details.documentsChangeTimestamp': {
                type: 'string',
            },
            'details.name2': {
                type: 'string',
            },
            'details.photosChangeTimestamp': {
                type: 'string',
            },
            'details.photosCount': {
                numericality: true,
            },
            'details.videosChangeTimestamp': {
                type: 'string',
            },
            'details.videosCount': {
                numericality: true,
            },
            'details.virtualTourUrlBranded': {
                type: 'string',
            },
            'details.virtualTourUrlUnbranded': {
                type: 'string',
            },
            'details.virtualTourUrlZillow': {
                type: 'string',
            },
            'details.documentsCount': {
                numericality: true,
            },
            'details.ownerName': {
                type: 'string',
            },
            'details.ownerPhone': {
                type: 'string',
            },
            'details.buyerAgentAOR': {
                type: 'string',
            },
            'details.buyerAgentCellPhone': {
                type: 'string',
            },
            'details.buyerAgentDirectPhone': {
                type: 'string',
            },
            'details.buyerAgentEmail': {
                type: 'string',
            },
            'details.buyerAgentFax': {
                type: 'string',
            },
            'details.buyerAgentFirstName': {
                type: 'string',
            },
            'details.buyerAgentFullName': {
                type: 'string',
            },
            'details.buyerAgentHomePhone': {
                type: 'string',
            },
            'details.buyerAgentKey': {
                type: 'string',
            },
            'details.buyerAgentKeyNumeric': {
                numericality: true,
            },
            'details.buyerAgentLastName': {
                type: 'string',
            },
            'details.buyerAgentMiddleName': {
                type: 'string',
            },
            'details.buyerAgentMlsId': {
                type: 'string',
            },
            'details.buyerAgentMobilePhone': {
                type: 'string',
            },
            'details.buyerAgentNamePrefix': {
                type: 'string',
            },
            'details.buyerAgentNameSuffix': {
                type: 'string',
            },
            'details.buyerAgentOfficePhone': {
                type: 'string',
            },
            'details.buyerAgentOfficePhoneExt': {
                type: 'string',
            },
            'details.buyerAgentPager': {
                type: 'string',
            },
            'details.buyerAgentPreferredPhone': {
                type: 'string',
            },
            'details.buyerAgentPreferredPhoneExt': {
                type: 'string',
            },
            'details.buyerAgentStateLicense': {
                type: 'string',
            },
            'details.buyerAgentTollFreePhone': {
                type: 'string',
            },
            'details.buyerAgentUrl': {
                type: 'string',
            },
            'details.buyerAgentVoiceMail': {
                type: 'string',
            },
            'details.buyerAgentVoiceMailExt': {
                type: 'string',
            },
            'details.buyerOfficeAOR': {
                type: 'string',
            },
            'details.buyerOfficeEmail': {
                type: 'string',
            },
            'details.buyerOfficeFax': {
                type: 'string',
            },
            'details.buyerOfficeKey': {
                type: 'string',
            },
            'details.buyerOfficeKeyNumeric': {
                numericality: true,
            },
            'details.buyerOfficeMlsId': {
                type: 'string',
            },
            'details.buyerOfficeName': {
                type: 'string',
            },
            'details.buyerOfficePhone': {
                type: 'string',
            },
            'details.buyerOfficePhoneExt': {
                type: 'string',
            },
            'details.buyerOfficeUrl': {
                type: 'string',
            },
            'details.buyerTeamKey': {
                type: 'string',
            },
            'details.buyerTeamKeyNumeric': {
                numericality: true,
            },
            'details.buyerTeamName': {
                type: 'string',
            },
            'details.closeDate': {
                type: 'string',
            },
            'details.purchaseContractDate': {
                type: 'string',
            },
            'details.closePrice': {
                numericality: true,
            },
            'details.coBuyerAgentAOR': {
                type: 'string',
            },
            'details.coBuyerAgentCellPhone': {
                type: 'string',
            },
            'details.coBuyerAgentDirectPhone': {
                type: 'string',
            },
            'details.coBuyerAgentEmail': {
                type: 'string',
            },
            'details.coBuyerAgentFax': {
                type: 'string',
            },
            'details.coBuyerAgentFirstName': {
                type: 'string',
            },
            'details.coBuyerAgentFullName': {
                type: 'string',
            },
            'details.coBuyerAgentHomePhone': {
                type: 'string',
            },
            'details.coBuyerAgentKey': {
                type: 'string',
            },
            'details.coBuyerAgentKeyNumeric': {
                numericality: true,
            },
            'details.coBuyerAgentLastName': {
                type: 'string',
            },
            'details.coBuyerAgentMiddleName': {
                type: 'string',
            },
            'details.coBuyerAgentMlsId': {
                type: 'string',
            },
            'details.coBuyerAgentMobilePhone': {
                type: 'string',
            },
            'details.coBuyerAgentNamePrefix': {
                type: 'string',
            },
            'details.coBuyerAgentNameSuffix': {
                type: 'string',
            },
            'details.coBuyerAgentOfficePhone': {
                type: 'string',
            },
            'details.coBuyerAgentOfficePhoneExt': {
                type: 'string',
            },
            'details.coBuyerAgentPager': {
                type: 'string',
            },
            'details.coBuyerAgentPreferredPhone': {
                type: 'string',
            },
            'details.coBuyerAgentPreferredPhoneExt': {
                type: 'string',
            },
            'details.coBuyerAgentStateLicense': {
                type: 'string',
            },
            'details.coBuyerAgentTollFreePhone': {
                type: 'string',
            },
            'details.coBuyerAgentUrl': {
                type: 'string',
            },
            'details.coBuyerAgentVoiceMail': {
                type: 'string',
            },
            'details.coBuyerAgentVoiceMailExt': {
                type: 'string',
            },
            'details.coBuyerOfficeAOR': {
                type: 'string',
            },
            'details.coBuyerOfficeEmail': {
                type: 'string',
            },
            'details.coBuyerOfficeFax': {
                type: 'string',
            },
            'details.coBuyerOfficeKey': {
                type: 'string',
            },
            'details.coBuyerOfficeKeyNumeric': {
                numericality: true,
            },
            'details.coBuyerOfficeMlsId': {
                type: 'string',
            },
            'details.coBuyerOfficeName': {
                type: 'string',
            },
            'details.coBuyerOfficePhone': {
                type: 'string',
            },
            'details.coBuyerOfficePhoneExt': {
                type: 'string',
            },
            'details.coBuyerOfficeUrl': {
                type: 'string',
            },
            'details.anchorsCoTenants': {
                type: 'string',
            },
            'details.buildingAreaSource': {
                type: 'string',
            },
            'details.buildingAreaTotal': {
                numericality: true,
            },
            'details.buildingAreaUnits': {
                type: 'string',
            },
            'details.buildingName': {
                type: 'string',
            },
            'details.capRate': {
                numericality: true,
            },
            'details.grossIncome': {
                numericality: true,
            },
            'details.grossScheduledIncome': {
                numericality: true,
            },
            'details.insuranceExpense': {
                numericality: true,
            },
            'details.leasableArea': {
                numericality: true,
            },
            'details.leasableAreaUnits': {
                type: 'string',
            },
            'details.leaseAmount': {
                numericality: true,
            },
            'details.leaseAmountFrequency': {
                type: 'string',
            },
            'details.leaseAssignableYn': {
                type: 'string',
            },
            'details.licensesExpense': {
                numericality: true,
            },
            'details.maintenanceExpense': {
                numericality: true,
            },
            'details.managerExpense': {
                numericality: true,
            },
            'details.netOperatingIncome': {
                numericality: true,
            },
            'details.newTaxesExpense': {
                numericality: true,
            },
            'details.numberOfSeparateElectricMeters': {
                numericality: true,
            },
            'details.numberOfSeparateGasMeters': {
                numericality: true,
            },
            'details.numberOfSeparateWaterMeters': {
                numericality: true,
            },
            'details.numberOfUnitsInCommunity': {
                numericality: true,
            },
            'details.numberOfUnitsLeased': {
                numericality: true,
            },
            'details.numberOfUnitsMoMo': {
                numericality: true,
            },
            'details.numberOfUnitsTotal': {
                numericality: true,
            },
            'details.numberOfUnitsVacant': {
                numericality: true,
            },
            'details.operatingExpense': {
                numericality: true,
            },
            'details.otherExpense': {
                numericality: true,
            },
            'details.parkName': {
                type: 'string',
            },
            'details.pestControlExpense': {
                numericality: true,
            },
            'details.professionalManagementExpense': {
                numericality: true,
            },
            'details.suppliesExpense': {
                numericality: true,
            },
            'details.totalActualRent': {
                numericality: true,
            },
            'details.vacancyAllowance': {
                numericality: true,
            },
            'details.vacancyAllowanceRate': {
                numericality: true,
            },
            'details.workmansCompensationExpense': {
                numericality: true,
            },
            'details.cableTvExpense': {
                numericality: true,
            },
            'details.electricExpense': {
                numericality: true,
            },
            'details.gardenerExpense': {
                numericality: true,
            },
            'details.gardnerExpense': {
                numericality: true,
            },
            'details.unitNumber': {
                type: 'string',
            },
            'details.unitsFurnished': {
                type: 'string',
            },
            'details.unitTypeTypeActualRent': {
                numericality: true,
            },
            'details.unitTypeTypeBathsTotal': {
                numericality: true,
            },
            'details.unitTypeTypeBedsTotal': {
                numericality: true,
            },
            'details.unitTypeTypeDescription': {
                type: 'string',
            },
            'details.unitTypeTypeFurnished': {
                type: 'string',
            },
            'details.unitTypeTypeGarageAttachedYn': {
                type: 'string',
            },
            'details.unitTypeTypeGarageSpaces': {
                numericality: true,
            },
            'details.unitTypeTypeProForma': {
                numericality: true,
            },
            'details.unitTypeTypeTotalRent': {
                numericality: true,
            },
            'details.unitTypeTypeUnitsTotal': {
                numericality: true,
            },
            'details.fuelExpense': {
                numericality: true,
            },
            'details.furnitureReplacementExpense': {
                numericality: true,
            },
            'details.poolExpense': {
                numericality: true,
            },
            'details.landLeaseAmount': {
                numericality: true,
            },
            'details.landLeaseAmountFrequency': {
                type: 'string',
            },
            'details.landLeaseExpirationDate': {
                type: 'string',
            },
            'details.landLeaseYn': {
                type: 'string',
            },
            'details.license1': {
                type: 'string',
            },
            'details.license2': {
                type: 'string',
            },
            'details.license3': {
                type: 'string',
            },
            'details.mobileDimUnits': {
                type: 'string',
            },
            'details.mobileHomeRemainsYn': {
                type: 'string',
            },
            'details.mobileLength': {
                numericality: true,
            },
            'details.mobileWidth': {
                numericality: true,
            },
            'details.make': {
                type: 'string',
            },
            'details.model': {
                type: 'string',
            },
            'details.numberOfPads': {
                numericality: true,
            },
            'details.parkManagerName': {
                type: 'string',
            },
            'details.parkManagerPhone': {
                type: 'string',
            },
            'details.serialU': {
                type: 'string',
            },
            'details.serialX': {
                type: 'string',
            },
            'details.serialXX': {
                type: 'string',
            },
            'details.doh1': {
                type: 'string',
            },
            'details.doh2': {
                type: 'string',
            },
            'details.doh3': {
                type: 'string',
            },
            'details.businessName': {
                type: 'string',
            },
            'details.hoursDaysOfOperationDescription': {
                type: 'string',
            },
            'details.leaseExpiration': {
                type: 'string',
            },
            'details.leaseRenewalOptionYn': {
                type: 'string',
            },
            'details.numberOfFullTimeEmployees': {
                numericality: true,
            },
            'details.numberOfPartTimeEmployees': {
                numericality: true,
            },
            'details.ownershipType': {
                type: 'string',
            },
            'details.seatingCapacity': {
                numericality: true,
            },
            'details.yearEstablished': {
                numericality: true,
            },
            'details.yearsCurrentOwner': {
                numericality: true,
            },
            'details.commonInterest': {
                type: 'string',
            },
            'details.rentControlYn': {
                type: 'string',
            },
            'details.rvParkingDimensions': {
                type: 'string',
            },
            'details.seniorCommunityYn': {
                type: 'string',
            },
            'details.occupantName': {
                type: 'string',
            },
            'details.occupantPhone': {
                type: 'string',
            },
            'details.occupantType': {
                type: 'string',
            },
            'details.maloneId': {
                numericality: true,
            },
            'details.furnished': {
                type: 'string',
            },
            'details.leaseTerm': {
                type: 'string',
            },
            'details.coordinates': {
                type: 'object'
            },
            'details.architecturalStyle': {
                type: 'array'
            },
            'details.propertyCondition': {
                type: 'array'
            },
            'details.currentUse': {
                type: 'array'
            },
            'details.developmentStatus': {
                type: 'array'
            },
            'details.possibleUse': {
                type: 'array'
            },
            'details.levels': {
                type: 'array'
            },
            'details.structureType': {
                type: 'array'
            },
            'details.constructionMaterials': {
                type: 'array'
            },
            'details.foundationDetails': {
                type: 'array'
            },
            'details.basement': {
                type: 'array'
            },
            'details.patioAndPorchFeatures': {
                type: 'array'
            },
            'details.poolFeatures': {
                type: 'array'
            },
            'details.roof': {
                type: 'array'
            },
            'details.windowFeatures': {
                type: 'array'
            },
            'details.exteriorFeatures': {
                type: 'array'
            },
            'details.appliances': {
                type: 'array'
            },
            'details.fireplaceFeatures': {
                type: 'array'
            },
            'details.flooring': {
                type: 'array'
            },
            'details.interiorFeatures': {
                type: 'array'
            },
            'details.laundryFeatures': {
                type: 'array'
            },
            'details.roomTypeFeatures': {
                type: 'array'
            },
            'details.roomType.xa2s1zjiivy4xay1Area': {
                numericality: true,
            },
            'details.roomType.xa2s1zjiivy4xay1AreaSource': {
                type: 'string',
            },
            'details.roomType.xa2s1zjiivy4xay1AreaUnits': {
                type: 'string',
            },
            'details.roomType.xa2s1zjiivy4xay1Description': {
                type: 'string',
            },
            'details.roomType.xa2s1zjiivy4xay1Dimensions': {
                type: 'string',
            },
            'details.roomType.xa2s1zjiivy4xay1Length': {
                numericality: true,
            },
            'details.roomType.xa2s1zjiivy4xay1LengthWidthSource': {
                type: 'string',
            },
            'details.roomType.xa2s1zjiivy4xay1LengthWidthUnits': {
                type: 'string',
            },
            'details.roomType.xa2s1zjiivy4xay1Level': {
                type: 'string',
            },
            'details.roomType.xa2s1zjiivy4xay1Width': {
                numericality: true,
            },
            'details.roomType.xa2s1zjiivy4xay1Features': {
                type: 'array'
            },
            'details.spaFeatures': {
                type: 'array'
            },
            'details.parkingFeatures': {
                type: 'array'
            },
            'details.cooling.wstwsfxqp2uv3igeYn': {
                type: 'string',
            },
            'details.electric.dbcrkc5o8ntrnsueOnPropertyYn': {
                type: 'string',
            },
            'details.electric.dbcrkc5o8ntrnsueExpense': {
                numericality: true,
            },
            'details.gas': {
                type: 'array'
            },
            'details.heating.wwkv4qqrd3g7maltYn': {
                type: 'string',
            },
            'details.powerProductionType.t5qybopjomywapirAnnual': {
                numericality: true,
            },
            'details.powerProductionType.t5qybopjomywapirAnnualStatus': {
                type: 'string',
            },
            'details.powerProductionType.t5qybopjomywapirSize': {
                numericality: true,
            },
            'details.powerProductionType.t5qybopjomywapirYearInstall': {
                numericality: true,
            },
            'details.telephone': {
                type: 'array'
            },
            'details.utilities': {
                type: 'array'
            },
            'details.waterSource': {
                type: 'array'
            },
            'details.greenBuildingVerificationType': {
                type: 'array'
            },
            'details.greenEnergyEfficient': {
                type: 'array'
            },
            'details.greenEnergyGeneration': {
                type: 'array'
            },
            'details.greenIndoorAirQuality': {
                type: 'array'
            },
            'details.greenLocation': {
                type: 'array'
            },
            'details.greenSustainability': {
                type: 'array'
            },
            'details.greenWaterConservation': {
                type: 'array'
            },
            'details.fencing': {
                type: 'array',
            },
            'details.frontageType': {
                type: 'array'
            },
            'details.horseAmenities': {
                type: 'array'
            },
            'details.irrigationSource': {
                type: 'array'
            },
            'details.lotFeatures': {
                type: 'array'
            },
            'details.otherStructures': {
                type: 'array'
            },
            'details.vegetation': {
                type: 'array'
            },
            'details.waterfrontFeatures': {
                type: 'array'
            },
            'details.roadFrontageType': {
                type: 'array'
            },
            'details.roadResponsibility': {
                type: 'array'
            },
            'details.roadSurfaceType': {
                type: 'array'
            },
            'details.view.n8weoi44kq1ve015Yn': {
                type: 'string',
            },
            'details.associationAmenities': {
                type: 'array'
            },
            'details.associationFeeIncludes': {
                type: 'array'
            },
            'details.communityFeatures': {
                type: 'array'
            },
            'details.taxExemptions': {
                type: 'array'
            },
            'details.taxStatusCurrent': {
                type: 'array'
            },
            'details.currentFinancing': {
                type: 'array'
            },
            'details.disclosures': {
                type: 'array'
            },
            'details.listAgentDesignation': {
                type: 'array'
            },
            'details.listingTerms': {
                type: 'array'
            },
            'details.lockBoxType': {
                type: 'array'
            },
            'details.specialListingConditions': {
                type: 'array'
            },
            'details.coListAgentDesignation': {
                type: 'array'
            },
            'details.leaseRenewalCompensation': {
                type: 'array'
            },
            'details.showingContactType': {
                type: 'array'
            },
            'details.showingDays': {
                type: 'array'
            },
            'details.showingRequirements': {
                type: 'array'
            },
            'details.syndicateTo': {
                type: 'array'
            },
            'details.listingInputOriginalMedia.changedByMemberId': {
                type: 'string',
            },
            'details.listingInputOriginalMedia.changedByMemberKeyNumeric': {
                numericality: true,
            },
            'details.listingInputOriginalMedia.className': {
                type: 'string',
            },
            'details.listingInputOriginalMedia.group': {
                type: 'string',
            },
            'details.listingInputOriginalMedia.id': {
                type: 'string',
            },
            'details.listingInputOriginalMedia.imageHeight': {
                numericality: true,
            },
            'details.listingInputOriginalMedia.imageWidth': {
                numericality: true,
            },
            'details.listingInputOriginalMedia.imageSizeDescription': {
                type: 'string',
            },
            'details.listingInputOriginalMedia.longDescription': {
                type: 'string',
            },
            'details.listingInputOriginalMedia.mediaCategory': {
                type: 'string',
            },
            'details.listingInputOriginalMedia.mediaHTML': {
                type: 'string',
            },
            'details.listingInputOriginalMedia.mediaKey': {
                type: 'string',
            },
            'details.listingInputOriginalMedia.mediaKeyNumeric': {
                numericality: true,
            },
            'details.listingInputOriginalMedia.mediaModificationTimestamp': {
                type: 'string',
            },
            'details.listingInputOriginalMedia.mediaObjectId': {
                type: 'string',
            },
            'details.listingInputOriginalMedia.mediaStatus': {
                type: 'string',
            },
            'details.listingInputOriginalMedia.mediaType': {
                type: 'string',
            },
            'details.listingInputOriginalMedia.mediaUrl': {
                type: 'string',
            },
            'details.listingInputOriginalMedia.modificationTimestamp': {
                type: 'string',
            },
            'details.listingInputOriginalMedia.order': {
                numericality: true,
            },
            'details.listingInputOriginalMedia.originatingSystemId': {
                type: 'string',
            },
            'details.listingInputOriginalMedia.originatingSystemMediaKey': {
                type: 'string',
            },
            'details.listingInputOriginalMedia.originatingSystemName': {
                type: 'string',
            },
            'details.listingInputOriginalMedia.preferredPhotoYn': {
                type: 'string',
            },
            'details.listingInputOriginalMedia.resizeMediaUrl': {
                type: 'string',
            },
            'details.listingInputOriginalMedia.resourceName': {
                type: 'string',
            },
            'details.listingInputOriginalMedia.resourceRecordId': {
                type: 'string',
            },
            'details.listingInputOriginalMedia.resourceRecordKey': {
                type: 'string',
            },
            'details.listingInputOriginalMedia.resourceRecordKeyNumeric': {
                numericality: true,
            },
            'details.listingInputOriginalMedia.shortDescription': {
                type: 'string',
            },
            'details.listingInputOriginalMedia.sourceSystemId': {
                type: 'string',
            },
            'details.listingInputOriginalMedia.sourceSystemMediaKey': {
                type: 'string',
            },
            'details.listingInputOriginalMedia.sourceSystemName': {
                type: 'string',
            },
            'details.listingInputOriginalMedia.imageOf': {
                type: 'array'
            },
            'details.listingInputOriginalMedia.permission': {
                type: 'array'
            },
            'details.media.changedByMemberId': {
                type: 'string',
            },
            'details.media.changedByMemberKey': {
                type: 'string',
            },
            'details.media.changedByMemberKeyNumeric': {
                numericality: true,
            },
            'details.media.className': {
                type: 'string',
            },
            'details.media.group': {
                type: 'string',
            },
            'details.media.id': {
                type: 'string',
            },
            'details.media.imageHeight': {
                numericality: true,
            },
            'details.media.imageOf': {
                type: 'array'
            },
            'details.media.imageSizeDescription': {
                type: 'string',
            },
            'details.media.imageWidth': {
                numericality: true,
            },
            'details.media.longDescription': {
                type: 'string',
            },
            'details.media.mediaCategory': {
                type: 'string',
            },
            'details.media.mediaHTML': {
                type: 'string',
            },
            'details.media.mediaKey': {
                type: 'string',
            },
            'details.media.mediaKeyNumeric': {
                numericality: true,
            },
            'details.media.mediaModificationTimestamp': {
                type: 'string',
            },
            'details.media.mediaObjectId': {
                type: 'string',
            },
            'details.media.mediaStatus': {
                type: 'string',
            },
            'details.media.mediaType': {
                type: 'string',
            },
            'details.media.mediaUrl': {
                type: 'string',
            },
            'details.media.modificationTimestamp': {
                type: 'string',
            },
            'details.media.order': {
                numericality: true,
            },
            'details.media.originatingSystemId': {
                type: 'string',
            },
            'details.media.originatingSystemMediaKey': {
                type: 'string',
            },
            'details.media.originatingSystemName': {
                type: 'string',
            },
            'details.media.permission': {
                type: 'array'
            },
            'details.media.preferredPhotoYn': {
                type: 'string',
            },
            'details.media.resizeMediaUrl': {
                type: 'string',
            },
            'details.media.resourceName': {
                type: 'string',
            },
            'details.media.resourceRecordId': {
                type: 'string',
            },
            'details.media.resourceRecordKey': {
                type: 'string',
            },
            'details.media.resourceRecordKeyNumeric': {
                numericality: true,
            },
            'details.media.shortDescription': {
                type: 'string',
            },
            'details.media.sourceSystemId': {
                type: 'string',
            },
            'details.media.sourceSystemMediaKey': {
                type: 'string',
            },
            'details.media.sourceSystemName': {
                type: 'string',
            },
            'details.documentsAvailable': {
                type: 'array'
            },
            'details.buyerAgentDesignation': {
                type: 'array'
            },
            'details.buyerFinancing': {
                type: 'array'
            },
            'details.coBuyerAgentDesignation': {
                type: 'array'
            },
            'details.buildingFeatures': {
                type: 'array'
            },
            'details.doorFeatures': {
                type: 'array'
            },
            'details.existingLeaseType': {
                type: 'array'
            },
            'details.financialDataSource': {
                type: 'array'
            },
            'details.incomeIncludes': {
                type: 'array'
            },
            'details.operatingExpenseIncludes': {
                type: 'array'
            },
            'details.petsAllowed': {
                type: 'array'
            },
            'details.tenantPays': {
                type: 'array'
            },
            'details.unitTypeType.7vec0d50ulxgmga0ActualRent': {
                numericality: true,
            },
            'details.unitTypeType.7vec0d50ulxgmga0BathsTotal': {
                numericality: true,
            },
            'details.unitTypeType.7vec0d50ulxgmga0BedsTotal': {
                numericality: true,
            },
            'details.unitTypeType.7vec0d50ulxgmga0Description': {
                type: 'string',
            },
            'details.unitTypeType.7vec0d50ulxgmga0Furnished': {
                type: 'string',
            },
            'details.unitTypeType.7vec0d50ulxgmga0GarageAttachedYn': {
                type: 'string',
            },
            'details.unitTypeType.7vec0d50ulxgmga0GarageSpaces': {
                numericality: true,
            },
            'details.unitTypeType.7vec0d50ulxgmga0ProForma': {
                numericality: true,
            },
            'details.unitTypeType.7vec0d50ulxgmga0TotalRent': {
                numericality: true,
            },
            'details.unitTypeType.7vec0d50ulxgmga0UnitsTotal': {
                numericality: true,
            },
            'details.ownerPays': {
                type: 'array'
            },
            'details.rentIncludes': {
                type: 'array'
            },
            'details.sewer': {
                type: 'array'
            },
            'details.skirt': {
                type: 'array'
            },
            'details.bodyType': {
                type: 'array'
            },
            'details.businessType': {
                type: 'array'
            },
            'details.hoursDaysOfOperation.yqesafq7icne36baDescription': {
                type: 'string',
            },
            'details.laborInformation': {
                type: 'array'
            },
            'details.specialLicenses': {
                type: 'array'
            },
            'details.accessibilityFeatures': {
                type: 'array'
            },
            'details.commonWalls': {
                type: 'array'
            },
            'details.securityFeatures': {
                type: 'array'
            },
            'details.otherEquipment': {
                type: 'array'
            },
            'details.possession': {
                type: 'array'
            },
            'details.schools': {
                type: 'array'
            },
            importSource: {
                type: 'string',
            },
            emails: {
                type: 'array'
            },
            phone: {
                type: 'array'
            },
            addresses: {
                type: 'array'
            },
            originalChangedAt: {
                datetime: true,
            },
            location: {
                type: 'object',
            },
            offMarketAt: {
                datetime: true,
            },
            listingType: {
                type: 'string',
            },
            stories: {
                numericality: true,
            },
        }
    }

    /**
     * @returns {{elasticsearch: *, search: *, restore: *, fetch: *, create: *, update: *, delete: *}}
     */
    apiPaths() {
        return {
            'homejunction-find': Engine.apiUrl(this.appResourceApiPrefix()) + '/homejunction-find',
            'elasticsearch': Engine.apiUrl(this.appResourceApiPrefix()) + '/property-search',
            ...super.apiPaths(),
        }
    }

    async homejunctionFind(data) {
        if (isEmpty(data.mls)) {
            return {};
        }

        if (isEmpty(data.id) && isEmpty(data.address)) {
            return {};
        }

        const url = this.apiPaths()['homejunction-find'];
        const apiResponse = await this.enginePost(url, data);

        const property = get(apiResponse, 'rawData.data');

        return property;
    }

    get images() {
        return Array.isArray(this.details?.images) ? this.details.images : [];
    }

    validateImage(index) {
        return new Promise((resolve, reject) => {
            const TIMEOUT = 12000; //it usually takes about 10 - 11 seconds for a hj image to load
            let timer, img = new Image();
            img.crossOrigin = "Anonymous";
            img.onerror = img.onabort = () => {
                clearTimeout(timer);
                reject('error');
            };
            img.onload = () => {
                clearTimeout(timer);
                resolve(this.images[index]);
            };
            timer = setTimeout(() => {
                // reset .src to invalid URL so it stops previous
                // loading, but doesn't trigger new load
                img.src = "/";
                reject('timeout');
            }, TIMEOUT);
            img.src = this.images[index];
        });
    }

    getValidImages(limit = -1) {
        return new Promise((resolve, reject) => {
            if (!this.images.length) {
                reject(`Property with uuid: ${this.uuid} has no images!`);
            }
            let length = limit >= 0 ? limit : this.images.length;
            let validImages = [];
            let promises = [];
            for (let index = 0; index < length; index++) {
                promises.push(this.validateImage(index))
            }
            Promise.allSettled(promises).then((results) => {
                results
                    .filter(result => result.status === 'fulfilled')
                    .forEach(result => {
                        validImages.push(result.value)
                    })
                if (!validImages.length) {
                    reject(`Property with uuid: ${this.uuid} has no valid images!`);
                }
                resolve(validImages)
            })
        })
    }

    async getGooglePanorama() {
        const StreetViewService = window?.google?.maps?.StreetViewService;
        if (!StreetViewService) {
            return new Promise((resolve, reject) => {
                reject('Google maps StreetViewService not found!');
            })
        }

        let panoIds = [];
        return new StreetViewService()
            .getPanorama({
                location: {
                    lat: this.details.latitude,
                    lng: this.details.longitude
                },
                preference: window.google.maps.StreetViewPreference.NEAREST,
                radius: 50,
            })
            .then(({data}) => {
                if (data?.location?.pano) {
                    panoIds.push(data.location.pano)
                }
                if (data?.links.length) {
                    data.links.forEach(link => {
                        if (link?.pano) {
                            panoIds.push(link?.pano)
                        }
                    })
                }
                return panoIds;
            })
    }

    async getImages(limit = -1) {
        let images = [];
        try {
            images = await this.getValidImages(limit)
        } catch (e) {
            // console.error(e)
        }
        if (limit >= 0 && images.length === limit) {
            return images;
        }

        let googlePanoramas = [];
        try {
            googlePanoramas = await this.getGooglePanorama();
        } catch (e) {
            console.error(e, `Property uuid: ${this.uuid}`)
        }
        googlePanoramas.forEach(panorama => {
            images.push(this.getStreetViewLink(panorama))
        })

        return images;
    }

    getStreetViewLink(panorama) {
        return `${Engine.apiHost}/google/street/view/property/${this.uuid}?pano=${panorama}`;
    }

}

export default PropertyAppResource;
